/**
 * The refine control.
 *
 * Presentational on purpose — it owns no request and no text. The composer
 * holds the question, so the composer holds what happens to it; a button that
 * could rewrite the box from inside itself would be a second source of truth
 * for the one piece of state the reader is actively editing.
 */

import { LoaderCircle, Wand2 } from "lucide-react";
import { Button } from "../ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip.tsx";

interface RefineButtonProps {
  readonly onClick: () => void;
  readonly busy: boolean;
  /** Off when there is no question yet, or one long enough to speak for itself. */
  readonly disabled: boolean;
}

export function RefineButton({ onClick, busy, disabled }: RefineButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* The wrapper keeps the tooltip reachable while the button is
            disabled — a disabled control fires no pointer events, and
            "why is this greyed out?" is exactly when the explanation is
            wanted. */}
        <span className="inline-flex">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClick}
            disabled={disabled || busy}
            aria-label="Refine question"
            aria-busy={busy}
            className="text-muted-foreground hover:text-foreground"
          >
            {busy ? (
              <LoaderCircle className="animate-spin motion-reduce:animate-none" />
            ) : (
              <Wand2 />
            )}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-72">
        Rewrite your question with what you've already studied made explicit.
        It lands in the box for you to edit — nothing is sent.
      </TooltipContent>
    </Tooltip>
  );
}
